import { createPortal } from 'react-dom';

import { canUseDom } from '@enterprise/core';

import type { PortalProps } from './types/portal.props';

/**
 * Renders React children into a DOM node that exists outside the DOM hierarchy of the parent component.
 *
 * Uses React's `createPortal` to render the children into the specified DOM node, or `document.body` by default.
 * If the DOM is not available (e.g., during server-side rendering), renders nothing.
 *
 * @param children - The React nodes to render into the portal.
 * @param node - Optional. The DOM node to render the portal into. Defaults to `document.body` if not provided.
 * @returns The portal containing the children, or `null` if DOM is not available.
 */
export const Portal = ({ children, node }: PortalProps) => {
  if (!canUseDom) {
    return null;
  }

  return createPortal(children, node ?? document.body);
};
